import type { IBet } from '../@types/Bet.js';
import type { IBoard } from '../@types/Board.js';
import type { Customer } from '../@types/Customer.js';
import type { ITSPayloadBet } from '../@types/ITSPayload.js';
import { Payable } from '../@types/Payable.js';
import type { Product } from '../@types/Product.js';
import { WagerError } from '../error/WagerError.js';
import type Bet from './bet/Bet.js';
import Bet1X2 from './bet/Bet1x2.js';
import BetChallenge from './bet/BetChallenge.js';
import BetFulltraff from './bet/BetFulltraff.js';
import BetNumberBoards from './bet/BetNumboards.js';
import BetScore from './bet/BetScore.js';
import BetSportkryss from './bet/BetSportkryss.js';

type ITSWager = {
  crc: number;
  competitionId: number[] | undefined;
  marketInfo: number;
  bagType: number;
  commissionPercent: number | undefined;
  bets: ITSPayloadBet<object>[];
};

/**
 * A class representing a wager.
 *
 * @class
 */
export class Wager extends Payable {
  private readonly _bets: IBet<IBoard>[] = [];

  private _bagType = 0;

  private _activatedDrawId?: number;

  private _commissionPercent?: number;

  private _customer: Customer = 'Player';

  private readonly _competitionId: number[] = [];

  private _competitionSignUp = false;

  private _addonProduct?: Product;

  private _couponIndex?: number;

  /**
   * Returns a coupon index for the wager. Used for multicoupon.
   *
   * @returns - The coupon index for the wager
   */
  get couponIndex(): number | undefined {
    return this._couponIndex;
  }

  /**
   * Returns first productBet for wager, sportkryss is not included as productBet.
   *
   * @returns - The first productBet for wager that is not Sportkryss
   */
  get productBet(): IBet<IBoard> | undefined {
    return this.bets.find((bet) => bet.product !== 'Sportkrysset');
  }

  /**
   * Returns productId for product where addon is purchased.
   *
   * @returns - The product for product where addon is purchased
   */
  get addonProduct(): Product | undefined {
    return this._addonProduct;
  }

  /**
   * Return boolean if is competitionSignUp.
   *
   * @returns - Boolean if is competitionSignUp
   */
  get isCompetitionSignUp(): boolean {
    return this._competitionSignUp;
  }

  /**
   * Returns a list of competitionIds.
   *
   * @returns - The list of competitionIds
   */
  get competitionId(): number[] {
    return this._competitionId;
  }

  /**
   * Returns activatedDrawId for wager.
   *
   * @returns - The activatedDrawId for wager
   */
  get activatedDrawId(): number | undefined {
    return this._activatedDrawId;
  }

  /**
   * Return bagType within the wager.
   *
   * @returns - bagType for the wager.
   */
  get bagType(): number {
    return this._bagType;
  }

  /**
   * Returns commissionPercent for wager.
   *
   * @returns - The commissionPercent for wager
   */
  get commissionPercent(): number | undefined {
    return this._commissionPercent;
  }

  /**
   * Returns role for wager instance.
   *
   * @returns - The role for wager instance
   */
  get customer(): Customer {
    return this._customer;
  }

  /**
   * Returns boolean if wager has own rows.
   *
   * @returns - Boolean if wager has own rows
   */
  get hasOwnRows(): boolean {
    return this.bets.some((bet) => bet.isOwnRows);
  }

  /**
   * Returns an array of bets in the wager.
   *
   * @returns - The array of bets in the wager.
   */
  get bets(): IBet<IBoard>[] {
    return this._bets;
  }

  /**
   * Returns total price of a wager.
   *
   * @returns - The total price of the wager.
   */
  get price(): number {
    return this.bets.reduce((sum, bet) => sum + bet.price, 0);
  }

  /**
   * Adds a bet to the wager.
   *
   * @param product - The product ID for the bet.
   * @param betBuilderFn - A function used to build the bet.
   * @throws {WagerError} - If the product ID is unsupported.
   * @returns - The updated Wager instance.
   */
  addBet(
    product: Extract<
      Product,
      'Powerplay' | 'Stryktipset' | 'Topptipset' | 'TopptipsetStryk' | 'TopptipsetExtra' | 'Europatipset'
    >,
    betBuilderFn: (bet: Bet1X2) => void
  ): this;
  addBet(product: Extract<Product, 'Bomben' | 'Matchen'>, betBuilderFn: (bet: BetScore) => void): this;
  addBet(product: Extract<Product, 'Fulltraff'>, betBuilderFn: (bet: BetFulltraff) => void): this;
  addBet(product: Extract<Product, 'Challenge'>, betBuilderFn: (bet: BetChallenge) => void): this;
  addBet(product: Extract<Product, 'Maltipset'>, betBuilderFn: (bet: BetNumberBoards) => void): this;
  addBet(product: Extract<Product, 'Sportkrysset'>, betBuilderFn: (bet: BetSportkryss) => void): this;
  addBet<TBet extends Bet<IBoard>>(product: Product, betBuilderFn: (bet: TBet) => void): this {
    let bet: IBet<IBoard>;

    switch (product) {
      case 'Bomben':
      case 'Matchen':
        bet = new BetScore(product);
        break;
      case 'Fulltraff':
        bet = new BetFulltraff(product);
        break;
      case 'Topptipset':
      case 'Powerplay':
      case 'TopptipsetStryk':
      case 'TopptipsetExtra':
      case 'Europatipset':
      case 'Stryktipset':
        bet = new Bet1X2(product);
        break;
      case 'Challenge':
        bet = new BetChallenge(product);
        break;
      case 'Maltipset':
        bet = new BetNumberBoards(product);
        break;
      case 'Sportkrysset':
        bet = new BetSportkryss(product);
        break;
      default:
        throw new WagerError({
          // Lint rule is added to catch new products that are not handled
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          message: `Unsupported product: ${product}`,
          clientCode: 'UNSUPPORTED_VALUE_PRODUCT_ID',
        });
    }

    this._bets.push(bet);

    betBuilderFn(bet as TBet);

    return this;
  }

  /**
   * Sets the coupon index for the wager. Used for multicoupon.
   *
   * @param couponIndex the given coupon index for the wager
   * @returns The updated Wager instance
   */
  setCouponIndex(couponIndex: number): this {
    this._couponIndex = couponIndex;
    return this;
  }

  /**
   * Sets productId for product where addon is purchased. Used for fulltraff on Stryktipset and T8Stryk.
   *
   * @param addonProductId productId for product where addon is purchased
   * @returns The updated Wager instance
   */
  setAddonProductId(addonProductId: Product): this {
    this._addonProduct = addonProductId;
    return this;
  }

  /**
   * Adds a competition to wager and bet.
   *
   * @param competitionId Competition id to add wager to
   * @param bet Bet instance
   * @param isSignUp Boolean if sign up in competition
   * @returns - The updated Wager instance.
   * @throws {WagerError} - If competitionId is missing.
   */
  addCompetitionForBet(competitionId: number, bet: IBet<IBoard>, isSignUp: boolean): this {
    if (!competitionId) {
      throw new WagerError({
        message: 'Missing competition id',
      });
    }
    // if (!(bet instanceof Bet)) {
    //   throw new WagerError({
    //     message: `Bet missing or not of type Bet`,
    //     clientCode: WagerError.ErrorCodes.GENERIC,
    //   });
    // }

    this.competitionId.push(competitionId);
    bet.addBetProperty('COMPETITION');
    this._competitionSignUp = isSignUp;

    return this;
  }

  /**
   * Returns true if competitionId(s) has been added to the wager.
   *
   * @returns - True if competitionId(s) has been added to the wager.
   */
  isCompetition(): boolean {
    return this.competitionId.length > 0;
  }

  /**
   * Returns a composed object representing the wager.
   *
   * @returns - The composed wager object.
   */
  compose(): ITSWager {
    const commissionPercent = this.commissionPercent ?? undefined;
    const bets = this.bets.map((bet) => {
      if (this.activatedDrawId) {
        bet.addBetProperty('GROUP');
      }
      if (this.customer === 'Retailer') {
        bet.addBetProperty('GROUP_B2C');
      }
      return bet.compose();
    });
    return {
      crc: 0,
      competitionId: this.competitionId.length > 0 ? this.competitionId : undefined,
      marketInfo: 0,
      bagType: this.bagType,
      commissionPercent,
      bets,
    };
  }

  /**
   * Set bagType on wager.
   *
   * @param type - The bagType for wager.
   * @returns - The updated Wager instance.
   */
  setBagType(type: number): this {
    this._bagType = type;

    return this;
  }

  /**
   * Set activatedDrawId for wager.
   *
   * @param activatedDrawId id for MP container
   * @returns - The updated Wager instance
   */
  setActivatedDrawId(activatedDrawId: number): this {
    this._activatedDrawId = activatedDrawId;

    return this;
  }

  /**
   * Sets commissionPercent for B2C Wagers.
   *
   * @param commissionPercent {Number} - commisionPercent for B2C wagers
   * @returns - The updated Wager instance
   */
  setCommissionPercent(commissionPercent: number): this {
    this._commissionPercent = commissionPercent;

    return this;
  }

  /**
   * Sets customer for wager.
   *
   * @param customer {Customer} - Sets customer on wager, either 'Player', 'Retailer' or 'Group'
   * @returns - The updated Wager instance
   */
  setCustomer(customer: Customer): this {
    this._customer = customer;

    return this;
  }

  /**
   * Function to check if wager is marketplace wager.
   *
   * @returns - True/false for if activatedDrawId is set
   */
  getIsMarketplaceWager(): boolean {
    return Boolean(this.activatedDrawId);
  }

  /**
   * Creates an array of product names within the wager for every Bet.
   *
   * @returns - Array of product names
   */
  getProductsInBet(): string[] {
    const productNames: string[] = [];
    for (const bet of this.bets) {
      if (bet.product !== 'Sportkrysset') {
        productNames.push(bet.productName);
      }
    }
    return productNames;
  }

  /**
   * Returns a string representation of the Wager instance.
   *
   * @returns - The String representation of a Wager
   */
  toString(): string {
    let logStr = '';
    if (this.getIsMarketplaceWager()) {
      logStr = ` S:${this.customer === 'Group' ? 'c' : 'r'}`;
      logStr += ` AD:${this.activatedDrawId}`;
    }

    return `B:${this.bets.length} ${this.bets.join(', ')}${logStr}`;
  }

  /**
   * Creates an JSON representation of the Wager instance, see `compose` for mor information.
   *
   * @returns - The JSON representation of a Wager
   */
  toJSON(): ITSWager {
    return this.compose();
  }

  /**
   * Returns a competition bet if present.
   *
   * @returns - The competition bet if present
   */
  getCompetitionBet(): IBet<IBoard> | undefined {
    return this.bets.find((bet) => bet.betProperties.includes('COMPETITION'));
  }
}

/**
 * Function to check if object is a Wager.
 *
 * @param obj The object to check
 * @returns True if the object is a Wager
 */
export function isWager(obj: unknown): obj is Wager {
  return obj instanceof Wager;
}
